/**
 * Shared single-flight refresh-token coordinator.
 *
 * Both api.js and ssApi.js perform 401 -> refresh logic. Because refresh
 * tokens are single-use (rotated per exchange), two concurrent 401s racing to
 * refresh would each POST the SAME refresh_token: the first rotates it, the
 * second sends a now-revoked token, fails, and logs the user out.
 *
 * This module guarantees that at most ONE refresh request is in flight at a
 * time, process-wide. Concurrent callers await the same promise and all read
 * the freshly-rotated access_token afterwards.
 *
 * SECURITY TODO (audit C1): tokens currently live in localStorage, which is
 * readable by any XSS payload. The correct fix is httpOnly, Secure,
 * SameSite=Strict cookies issued by the backend, which requires coordinated
 * backend changes (set-cookie on login/refresh, CSRF token handling). Until
 * that migration lands, keep logout fully clearing both tokens.
 */
import axios from 'axios';

let _inFlight = null;

/**
 * Refresh the access token at most once across all concurrent callers.
 *
 * @param {string} baseUrl Absolute base used to build the /auth/refresh URL.
 *   Pass the value WITHOUT a trailing /api/... suffix mismatch — callers
 *   supply the exact refresh endpoint base they need.
 * @returns {Promise<string|null>} the new access token, or null if no refresh
 *   token was available / the refresh failed.
 */
export function refreshAccessToken(refreshUrl) {
  if (_inFlight) return _inFlight;

  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) return Promise.resolve(null);

  _inFlight = axios
    .post(refreshUrl, { refresh_token: refresh })
    .then(({ data }) => {
      const newToken = data?.data?.access_token || data?.access_token;
      // Refresh tokens are single-use (rotated per exchange) — persist the new one.
      const newRefresh = data?.data?.refresh_token || data?.refresh_token;
      if (newToken) localStorage.setItem('access_token', newToken);
      if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
      return newToken || null;
    })
    .finally(() => {
      _inFlight = null;
    });

  return _inFlight;
}
